import React, { useMemo, useState } from "react";
import { ADVERTISER, OVERVIEW, advertiserRowClass } from "../../../../../core/theme/reportTheme";
import { BUSINESS_TYPE_LABELS } from "../../../../../core/data/businessTypes/labels";
import { VISIBILITY_CHANNELS } from "../../../../../core/data/marketOverviewText";
import { RIVAL_COLUMNS, rivalDefaultCompare } from "../../../../../core/data/advertiserSorts";
import { sortRows, nextSort } from "../../../../../core/data/sortRows";
import SortButton from "../../../components/SortButton";

const COMPACT_ROWS = 5;

// "Who you're up against": everyone seen in the same searches as the pinned
// advertiser (ads, map pack or organic). Shows 5; expanded shows everyone.
// Fixed column widths, so the narrow panel never scrolls sideways.
// highlightKeys: advertisers highlighted in yellow (from the Advertisers filter).
export default function RivalsPanel({ result, expandButton, expanded, highlightKeys = [] }) {
  const [sort, setSort] = useState(null);
  const rows = useMemo(() => sortRows(result.rivals, sort, RIVAL_COLUMNS, rivalDefaultCompare), [result.rivals, sort]);
  const shown = expanded ? rows : rows.slice(0, COMPACT_ROWS);
  const sortProps = { sort, onSort: column => setSort(nextSort(sort, column, RIVAL_COLUMNS)) };

  return (
    <div className={`${OVERVIEW.panel} flex-1`}>
      <div className={OVERVIEW.panelHeader}>
        <div className={OVERVIEW.panelTitleRow}>
          <h3 className={OVERVIEW.panelTitle}>Who you're up against</h3>
          <span className={OVERVIEW.panelNote}>seen in the same searches</span>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {sort && <button type="button" onClick={() => setSort(null)} className={OVERVIEW.link}>Back to default order</button>}
          {expandButton}
        </div>
      </div>
      <div className={OVERVIEW.panelScroll}>
        {rows.length === 0 ? (
          <p className="px-4 py-6 text-sm text-slate-400 text-center">
            {result.pinned.seen ? "Nobody else appeared in the same searches." : "Not seen, so no shared searches."}
          </p>
        ) : (
          <table className="w-full border-separate border-spacing-0 table-fixed">
            <colgroup>
              <col />
              <col className="w-28" />
              <col className="w-[7.75rem]" />
              <col className="w-[4.5rem]" />
            </colgroup>
            <thead>
              <tr>
                <th className={ADVERTISER.thFirst}><SortButton column="advertiser" label="Advertiser" {...sortProps} /></th>
                <th className={ADVERTISER.th}><SortButton column="type" label="Type" {...sortProps} /></th>
                <th className={ADVERTISER.th} title={`Searches where both appeared (${VISIBILITY_CHANNELS}), of the pinned advertiser's searches`}>
                  <SortButton column="shared" label="In your searches" {...sortProps} />
                </th>
                <th className={ADVERTISER.th}><SortButton column="visibility" label="Visibility" {...sortProps} /></th>
              </tr>
            </thead>
            <tbody>
              {shown.map(r => {
                const look = advertiserRowClass(false, highlightKeys.includes(r.key));
                return (
                <tr key={r.key} className={look.row}>
                  <td className={`${ADVERTISER.tdFirst} ${look.first} text-slate-900 overflow-hidden text-ellipsis`} title={r.displayName}>{r.displayName}</td>
                  <td className={ADVERTISER.td} title={BUSINESS_TYPE_LABELS[r.businessType.type]}>
                    <span className="block truncate">{BUSINESS_TYPE_LABELS[r.businessType.type]}</span>
                  </td>
                  <td className={ADVERTISER.td} title={`In ${r.sharedSearches} of your ${r.of} searches`}>
                    <span className="flex items-center gap-2">
                      <span className={ADVERTISER.sharedTrack}><span className={`block ${ADVERTISER.sharedFill}`} style={{ width: `${(100 * r.sharedSearches) / r.of}%` }} /></span>
                      <span className="text-xs text-slate-600">{r.sharedSearches} of {r.of}</span>
                    </span>
                  </td>
                  <td className={`${ADVERTISER.td} font-semibold text-slate-900`}>{r.visibility.score}%</td>
                </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      {!expanded && rows.length > COMPACT_ROWS && (
        <div className={OVERVIEW.panelFooter}>Showing {COMPACT_ROWS} of {rows.length} · ⤢ for everyone</div>
      )}
    </div>
  );
}
